import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';
import type { Transfer } from '../queries/transfer.queries';

// ================================
// INPUT TYPES
// ================================

export interface CreateTransferInput {
  type: 'warehouse_to_branch' | 'branch_to_branch' | 'branch_to_warehouse';
  fromLocationId?: string; // Required for branch_to_branch
  toLocationId: string;
  items: Array<{
    ingredientId: string;
    requestedQuantity: number;
    unit: string;
    notes?: string;
  }>;
  notes?: string;
  expectedDeliveryDate?: string;
}

export interface HQApprovalInput {
  action: 'approve' | 'reject';
  reason?: string;
  modifiedItems?: Array<{
    itemId: string;
    approvedQuantity: number;
  }>;
}

export interface ApproveTransferInput {
  items: Array<{
    itemId: string;
    approvedQuantity: number;
    // FEFO override
    overrideFEFO?: boolean;
    overrideReason?: string;
    selectedBatches?: Array<{
      batchNumber?: string;
      expiryDate?: string;
      quantity: number;
    }>;
  }>;
  notes?: string;
  expectedDeliveryHours?: number;
}

export interface VerifyDispatchItemInput {
  itemId: string;
  scannedBarcode: string;
  scanType: 'supplier_barcode' | 'internal_qr' | 'manual';
}

export interface DispatchTransferInput {
  dispatchPhotos?: string[];
  notes?: string;
  skipVerification?: boolean;
  skipReason?: string;
}

export interface ReceiveTransferItemInput {
  itemId: string;
  receivedQuantity: number;
  condition?: 'good' | 'damaged' | 'expired' | 'wrong_item' | 'quantity_short' | 'quality_issue';
  isRejected?: boolean;
  rejectedQuantity?: number;
  rejectionReason?: string;
  rejectionPhotoUrls?: string[];
  notes?: string;
}

export interface ReceiveTransferInput {
  items: ReceiveTransferItemInput[];
  receivingPhotos?: string[];
  notes?: string;
}

export interface CreateReplacementTransferInput {
  items: Array<{
    ingredientId: string;
    quantity: number;
    unit: string;
    originalRejectionReason?: string;
  }>;
  notes?: string;
}

// ================================
// MUTATIONS
// ================================

/**
 * Create a new transfer request
 */
export function useCreateTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTransferInput) => {
      const response = await apiClient.post<APIResponse<{ transfer: Transfer }>>(
        '/transfers',
        data
      );
      return response.data.data.transfer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfers'] });
    },
  });
}

/**
 * HQ approval for branch-to-branch transfers
 */
export function useHQApproveTransfer(transferId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: HQApprovalInput) => {
      const response = await apiClient.patch<APIResponse<{ transfer: Transfer }>>(
        `/transfers/${transferId}/hq-approval`,
        data
      );
      return response.data.data.transfer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfers'] });
      queryClient.invalidateQueries({ queryKey: ['transfer', transferId] });
    },
  });
}

/**
 * Warehouse approval (with FEFO selection)
 */
export function useApproveTransfer(transferId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ApproveTransferInput) => {
      const response = await apiClient.patch<APIResponse<{ transfer: Transfer }>>(
        `/transfers/${transferId}/approve`,
        data
      );
      return response.data.data.transfer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfers'] });
      queryClient.invalidateQueries({ queryKey: ['transfer', transferId] });
    },
  });
}

/**
 * Reserve stock for approved transfer
 */
export function useReserveStock(transferId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.patch<APIResponse<{ transfer: Transfer }>>(
        `/transfers/${transferId}/reserve-stock`
      );
      return response.data.data.transfer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfers'] });
      queryClient.invalidateQueries({ queryKey: ['transfer', transferId] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

/**
 * Start dispatch verification (after picking)
 */
export function useStartDispatchVerification(transferId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.patch<APIResponse<{ transfer: Transfer }>>(
        `/transfers/${transferId}/start-dispatch-verification`
      );
      return response.data.data.transfer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfers'] });
      queryClient.invalidateQueries({ queryKey: ['transfer', transferId] });
    },
  });
}

/**
 * Verify individual item during dispatch (barcode scan)
 */
export function useVerifyDispatchItem(transferId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: VerifyDispatchItemInput) => {
      const response = await apiClient.patch<APIResponse<{
        transfer: Transfer;
        itemVerified: boolean;
        allVerified: boolean;
      }>>(
        `/transfers/${transferId}/verify-dispatch-item`,
        data
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfer', transferId] });
    },
  });
}

/**
 * Dispatch transfer (after verification)
 */
export function useDispatchTransfer(transferId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data?: DispatchTransferInput) => {
      const response = await apiClient.patch<APIResponse<{ transfer: Transfer }>>(
        `/transfers/${transferId}/dispatch`,
        data || {}
      );
      return response.data.data.transfer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfers'] });
      queryClient.invalidateQueries({ queryKey: ['transfer', transferId] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

/**
 * Receive transfer with item-level details and rejection handling
 */
export function useReceiveTransfer(transferId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ReceiveTransferInput) => {
      const response = await apiClient.patch<APIResponse<{
        transfer: Transfer;
        hasRejections: boolean;
        totalRejected: number;
      }>>(
        `/transfers/${transferId}/receive`,
        data
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfers'] });
      queryClient.invalidateQueries({ queryKey: ['transfer', transferId] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

/**
 * Complete a received transfer
 */
export function useCompleteTransfer(transferId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.patch<APIResponse<{ transfer: Transfer }>>(
        `/transfers/${transferId}/complete`
      );
      return response.data.data.transfer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfers'] });
      queryClient.invalidateQueries({ queryKey: ['transfer', transferId] });
    },
  });
}

/**
 * Create replacement transfer for rejected items
 */
export function useCreateReplacementTransfer(originalTransferId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateReplacementTransferInput) => {
      const response = await apiClient.post<APIResponse<{ transfer: Transfer }>>(
        `/transfers/${originalTransferId}/replacement`,
        data
      );
      return response.data.data.transfer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfers'] });
      queryClient.invalidateQueries({ queryKey: ['transfer', originalTransferId] });
    },
  });
}

/**
 * Cancel transfer
 */
export function useCancelTransfer(transferId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reason: string) => {
      const response = await apiClient.patch<APIResponse<{ transfer: Transfer }>>(
        `/transfers/${transferId}/cancel`,
        { reason }
      );
      return response.data.data.transfer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfers'] });
      queryClient.invalidateQueries({ queryKey: ['transfer', transferId] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

/**
 * Trigger SLA status update (admin only)
 */
export function useUpdateSLAStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<APIResponse<{ updatedCount: number }>>(
        '/transfers/update-sla-status'
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfers'] });
    },
  });
}

